// R-131-FIN-DECISION-I / R-131-FIN-DECISION-K — auto-generated pathway decision recommendation.

import {
  computeAllPathwayCompareMetrics,
  formatUsdCompactMillions,
  formatUsdPerMwh,
  type PathwayCompareMetrics,
} from './pathwayCompareMetrics';
import {
  CARBON_PENALTY_RANKING_USD_PER_TONNE,
  CONFIDENCE_NPV_BAND_WIDTH_HIGH,
  CONFIDENCE_NPV_BAND_WIDTH_MEDIUM,
  CONFIDENCE_NPV_GAP_HIGH,
  CONFIDENCE_NPV_GAP_MEDIUM,
  CONFIDENCE_PWIN_HIGH,
  CONFIDENCE_PWIN_MEDIUM,
  NPV_TIE_BAND_FRACTION,
  SENSITIVITY_LCOE_SPREAD_FRACTION,
  SENSITIVITY_NPV_SPREAD_FRACTION,
} from './finDecisionConstants';
import {
  DEFAULT_COMPARE_MONTE_CARLO_OPTS,
  runComparisonMonteCarlo,
} from './pathwayMonteCarlo';
import type { Pathway } from './types';
import type { ProjectIntent } from '../../types/siteRag';

export interface DecisionRecommendation {
  headline: string;
  confidence: 'high' | 'medium' | 'low';
  primaryReason: string;
  considerations: { positive: string[]; negative: string[] };
  sensitivityCallouts: string[];
  carbonCallouts?: string[];
  riskCallouts: string[];
  /**
   * R-131-FIN-DECISION-PWIN — ADDITIVE correlated-scenario win probabilities
   * from `runComparisonMonteCarlo`. Per pathway: the fraction of correlated
   * common-mode draws in which it had the lowest LCOE (pWinLcoe) / highest NPV
   * (pWinNpv). Surfaced so the UI can say "leads on NPV in 62% of correlated
   * futures". Optional: undefined for a single-pathway comparison (no contest).
   */
  pWin?: PathwayPWin[];
}

/** Correlated-scenario win probabilities for one pathway. */
export interface PathwayPWin {
  pathwayId: string;
  name: string;
  /** Fraction of correlated runs (0..1) where this had the lowest LCOE. */
  pWinLcoe: number;
  /** Fraction of correlated runs (0..1) where this had the highest NPV. */
  pWinNpv: number;
}

export interface PathwayComparisonResult {
  pathways: Pathway[];
  metrics: PathwayCompareMetrics[];
}

function isCarbonCommitmentBinding(
  carbonCommitment?: ProjectIntent['carbonCommitment'],
): boolean {
  return carbonCommitment != null && carbonCommitment !== 'none' && carbonCommitment !== 'custom';
}

/**
 * The carbon-adjusted score a pathway is ranked on, in present-value USD.
 *
 * C14 — the penalty was `annualTCo2e × $/t`: ONE YEAR of carbon cost subtracted
 * from a LIFETIME NPV, roughly an order of magnitude too small, so a binding
 * carbon commitment barely moved the order it exists to govern. It is now the
 * PV of the whole emissions stream (`carbonPvTCo2e`), discounted on the same
 * basis as the NPV, so the two terms are commensurate.
 */
function rankingScoreUsd(
  m: PathwayCompareMetrics,
  carbonBinding: boolean,
  carbonPenaltyPerTonneUsd: number,
): number {
  if (!carbonBinding) return m.npvUsd;
  const carbonPvTonnes = Number.isFinite(m.carbonPvTCo2e) ? m.carbonPvTCo2e : 0;
  return m.npvUsd - carbonPvTonnes * carbonPenaltyPerTonneUsd;
}

/**
 * Rank pathways best-first.
 *
 * C14 — TOTAL ORDER, DELIBERATELY. The previous comparator applied the NPV tie
 * band PAIRWISE: two pathways within `NPV_TIE_BAND_FRACTION` compared on
 * time-to-power, otherwise on NPV. That relation is not transitive — A can tie
 * B and B tie C while A and C are far enough apart to compare on NPV — so
 * `Array.prototype.sort` returned a different leader depending on INPUT ORDER.
 * That is the same class of defect as the order-dependent verdict (C1): the
 * recommendation moved when nothing about the project did.
 *
 * The tie band is preserved as an intent — "NPVs this close are not
 * meaningfully different; prefer the faster route" — but expressed as a
 * QUANTISATION computed ONCE for the whole set rather than per pair. Bucketing
 * is transitive, so the sort is a genuine total order, and it is closed with an
 * explicit id tie-break so equal-in-every-respect pathways still order
 * deterministically.
 */
function rankPathways(
  metrics: PathwayCompareMetrics[],
  carbonCommitment?: ProjectIntent['carbonCommitment'],
  carbonPenaltyPerTonneUsd: number = CARBON_PENALTY_RANKING_USD_PER_TONNE,
): PathwayCompareMetrics[] {
  const carbonBinding = isCarbonCommitmentBinding(carbonCommitment);
  const scoreOf = new Map<string, number>();
  for (const m of metrics) {
    scoreOf.set(m.pathwayId, rankingScoreUsd(m, carbonBinding, carbonPenaltyPerTonneUsd));
  }

  // ONE band width for the whole comparison, from the largest score in the set.
  // Computing it per pair is what broke transitivity.
  const maxAbsScore = metrics.reduce(
    (max, m) => Math.max(max, Math.abs(scoreOf.get(m.pathwayId) ?? 0)),
    0,
  );
  const bandWidth = maxAbsScore * NPV_TIE_BAND_FRACTION;

  const bucketOf = (m: PathwayCompareMetrics): number => {
    const score = scoreOf.get(m.pathwayId) ?? 0;
    // No spread in the set ⇒ every pathway is in one bucket and the order falls
    // through to time-to-power, which is what the tie band intends.
    return bandWidth > 0 ? Math.round(score / bandWidth) : 0;
  };

  return [...metrics].sort((a, b) => {
    const bucketGap = bucketOf(b) - bucketOf(a);
    if (bucketGap !== 0) return bucketGap;
    const timeGap = a.timeToPowerYears - b.timeToPowerYears;
    if (timeGap !== 0) return timeGap;
    return a.pathwayId.localeCompare(b.pathwayId);
  });
}


/** Monte-Carlo spread callouts, capped at three. Ranked order — see `buildCrossovers`. */
function monteCarloSensitivity(metrics: PathwayCompareMetrics[]): string[] {
  const callouts: string[] = [];
  for (const row of metrics) {
    const lcoeSpread = row.lcoeP90 - row.lcoeP10;
    const npvSpread = row.npvP90 - row.npvP10;
    if (lcoeSpread > row.lcoeRealUsdPerMwh * SENSITIVITY_LCOE_SPREAD_FRACTION) {
      callouts.push(
        `${row.name}: LCOE band ${formatUsdPerMwh(row.lcoeP10)}–${formatUsdPerMwh(row.lcoeP90)} — ranking sensitive to queue/CAPEX uncertainty.`,
      );
    }
    if (Math.abs(npvSpread) > Math.abs(row.npvUsd) * SENSITIVITY_NPV_SPREAD_FRACTION) {
      callouts.push(
        `${row.name}: NPV spans ${formatUsdCompactMillions(row.npvP10)} to ${formatUsdCompactMillions(row.npvP90)} under Monte Carlo.`,
      );
    }
  }
  return callouts.slice(0, 3);
}

const CONFIDENCE_RANK: Record<DecisionRecommendation['confidence'], number> = {
  low: 0,
  medium: 1,
  high: 2,
};

/** Return the lower (more conservative) of two confidence ratings. */
function moreConservativeConfidence(
  a: DecisionRecommendation['confidence'],
  b: DecisionRecommendation['confidence'],
): DecisionRecommendation['confidence'] {
  return CONFIDENCE_RANK[a] <= CONFIDENCE_RANK[b] ? a : b;
}

/**
 * Map the leader's correlated-scenario NPV win probability to a confidence
 * rating (R-131-FIN-DECISION-PWIN). A leader that wins only ~55% of correlated
 * runs is fragile — a single common-mode shock flips the ranking — so it caps
 * confidence at "low" even when the deterministic NPV gap looks comfortable.
 */
function pWinConfidence(pWin: number): DecisionRecommendation['confidence'] {
  if (pWin >= CONFIDENCE_PWIN_HIGH) return 'high';
  if (pWin >= CONFIDENCE_PWIN_MEDIUM) return 'medium';
  return 'low';
}

/** Rank pathways and emit a decision recommendation for report citation. */
export function recommendPathwayComparison(
  pathways: Pathway[],
  opts?: {
    now?: Date;
    carbonCommitment?: ProjectIntent['carbonCommitment'];
    carbonPricePerTUsd?: number;
    /** Correlated-MC iterations for the pWin signal (default 400). */
    pWinRuns?: number;
    /** Seed for the correlated-MC pWin signal (default 1, deterministic). */
    pWinSeed?: number;
  },
): DecisionRecommendation {
  const metrics = computeAllPathwayCompareMetrics(pathways, {
    now: opts?.now,
    carbonCommitment: opts?.carbonCommitment,
    carbonPricePerTUsd: opts?.carbonPricePerTUsd,
  });
  // If a caller supplies a specific shadow carbon price (opts.carbonPricePerTUsd),
  // honor it as the ranking penalty per tCO2e. Otherwise fall back to
  // CARBON_PENALTY_RANKING_USD_PER_TONNE, which now equals the documented
  // DEFAULT_CARBON_PRICE_USD_PER_T ($50/tCO2e) — corrected from the prior 1000x value.
  const ranked = rankPathways(
    metrics,
    opts?.carbonCommitment,
    opts?.carbonPricePerTUsd ?? CARBON_PENALTY_RANKING_USD_PER_TONNE,
  );
  const leader = ranked[0];
  const runnerUp = ranked[1];

  // R-131-FIN-DECISION-PWIN — correlated-scenario win probabilities. Only run
  // the comparison Monte Carlo when there is an actual contest (≥2 pathways);
  // a single pathway has no win/lose, so pWin stays undefined (no contest).
  const correlated =
    pathways.length > 1
      ? runComparisonMonteCarlo(pathways, {
          ...DEFAULT_COMPARE_MONTE_CARLO_OPTS,
          runs: opts?.pWinRuns ?? 400,
          seedRng: opts?.pWinSeed ?? 1,
          now: opts?.now,
        })
      : undefined;
  const pWin: PathwayPWin[] | undefined = correlated?.pathways.map((p) => ({
    pathwayId: p.pathwayId,
    name: p.name,
    pWinLcoe: p.pWinLcoe,
    pWinNpv: p.pWinNpv,
  }));
  const leaderPWinNpv =
    pWin?.find((p) => p.pathwayId === leader.pathwayId)?.pWinNpv;

  const headline = runnerUp
    ? `${leader.name} — ${leader.headline.label.replace(/^[^\s]+\s/, '')}, ${formatUsdPerMwh(leader.lcoeRealUsdPerMwh)} LCOE, ${formatUsdCompactMillions(leader.npvUsd)} NPV`
    : `${leader.name} — ${formatUsdPerMwh(leader.lcoeRealUsdPerMwh)} LCOE, ${formatUsdCompactMillions(leader.npvUsd)} NPV`;

  // Confidence is blended from TWO independent signals, then the MORE
  // CONSERVATIVE (lower) rating is taken:
  //   (1) NPV gap between leader and runner-up — how cleanly the winner separates.
  //   (2) Leader's Monte-Carlo NPV band width (P90 - P10) relative to |NPV| —
  //       a wide uncertainty band lowers confidence even if the gap looks large.
  // Rationale: a leader that "wins" by a comfortable margin but whose own NPV
  // could swing across a wide P10–P90 range is not a high-confidence call.
  const npvGap = runnerUp
    ? Math.abs(leader.npvUsd - runnerUp.npvUsd) / Math.max(Math.abs(leader.npvUsd), 1)
    : 1;
  const gapConfidence: DecisionRecommendation['confidence'] =
    npvGap >= CONFIDENCE_NPV_GAP_HIGH
      ? 'high'
      : npvGap >= CONFIDENCE_NPV_GAP_MEDIUM
        ? 'medium'
        : 'low';

  // Relative P10–P90 band width of the leader's NPV. Guard against a zero/near-
  // zero |NPV| denominator: an undefined band width yields no downgrade ('high').
  const leaderNpvBandWidth = Math.abs(leader.npvP90 - leader.npvP10);
  const leaderNpvMagnitude = Math.abs(leader.npvUsd);
  const relativeNpvBand =
    leaderNpvMagnitude > 0 ? leaderNpvBandWidth / leaderNpvMagnitude : 0;
  const bandConfidence: DecisionRecommendation['confidence'] =
    relativeNpvBand <= CONFIDENCE_NPV_BAND_WIDTH_HIGH
      ? 'high'
      : relativeNpvBand <= CONFIDENCE_NPV_BAND_WIDTH_MEDIUM
        ? 'medium'
        : 'low';

  // (3) Correlated-scenario win probability of the leader (R-131-FIN-DECISION-PWIN).
  //     A wide deterministic NPV gap can still be fragile: if a single common-mode
  //     shock (fuel/queue/CAPEX) flips the ranking, the leader wins only a thin
  //     majority of correlated runs. Folding pWinNpv in via the SAME "more
  //     conservative wins" rule caps confidence at the win-based rating. When pWin
  //     is unavailable (single pathway) this signal is omitted (no downgrade).
  const gapBandConfidence = moreConservativeConfidence(gapConfidence, bandConfidence);
  const confidence =
    leaderPWinNpv != null
      ? moreConservativeConfidence(gapBandConfidence, pWinConfidence(leaderPWinNpv))
      : gapBandConfidence;

  const primaryReason = runnerUp
    ? `${leader.name} leads on risk-adjusted NPV (${formatUsdCompactMillions(leader.npvUsd)} vs ${formatUsdCompactMillions(runnerUp.npvUsd)}) with ${leader.timeToPowerYears.toFixed(1)} yr time-to-power.`
    : `${leader.name} is the only pathway evaluated; NPV ${formatUsdCompactMillions(leader.npvUsd)} at ${formatUsdPerMwh(leader.lcoeRealUsdPerMwh)} LCOE.`;

  const positive = [
    `Lowest LCOE in set: ${formatUsdPerMwh(leader.lcoeRealUsdPerMwh)} (${leader.name}).`,
    `Fastest time-to-power: ${leader.timeToPowerYears.toFixed(1)} years (${leader.headline.label}).`,
    `Risk premium: +${leader.riskPremiumBps} bps WACC overlay.`,
  ];
  const negative = ranked.slice(1, 3).map(
    (row) => `${row.name}: ${formatUsdPerMwh(row.lcoeRealUsdPerMwh)} LCOE, ${formatUsdCompactMillions(row.npvUsd)} NPV, ${row.timeToPowerYears.toFixed(1)} yr to power.`,
  );

  const carbonCallouts =
    isCarbonCommitmentBinding(opts?.carbonCommitment)
      ? ranked
          .filter((row) => row.annualTCo2e > 0)
          .map((row) => `${row.name}: ${row.annualTCo2e.toLocaleString('en-US')} tCO₂e/yr — penalized under carbon commitment.`)
          .slice(0, 2)
      : undefined;

  return {
    headline,
    confidence,
    primaryReason,
    considerations: { positive, negative },
    sensitivityCallouts: monteCarloSensitivity(ranked),
    carbonCallouts,
    riskCallouts: ranked.slice(0, 2).map(
      (row) => `${row.name}: +${row.riskPremiumBps} bps risk premium on base WACC.`,
    ),
    pWin,
  };
}

/** One-line recommendation string for report footnotes and LLM directives. */
export function formatDecisionRecommendationSummary(rec: DecisionRecommendation): string {
  return rec.headline;
}
